// Development authentication bypass system
// Only active when NODE_ENV=LOCAL for testing purposes

export interface DevUser {
  id: string;
  email: string;
  role: 'client' | 'admin' | 'practitioner';
  first_name?: string;
  last_name?: string;
  display_name?: string;
  phone?: string;
  metadata?: any;
}

// Predefined dev users for testing different user types with realistic personas
export const DEV_USERS: Record<string, DevUser> = {
  'dev-client': {
    id: 'dev-client-001',
    email: 'sarah.chen@test.local',
    role: 'client',
    first_name: 'Sarah',
    last_name: 'Chen',
    display_name: 'Sarah Chen',
    phone: '+1-555-0123',
    metadata: {
      intake_completed: false,
      test_mode: true,
      persona: 'Sarah Chen - Marketing Manager',
      status_message: 'Exhausted professional seeking holistic solutions',
      test_scenarios: ['First-time intake', 'Assessment completion', 'Resource access']
    }
  },
  'dev-admin': {
    id: 'dev-admin-001', 
    email: 'dr.chen@dev.local',
    role: 'admin',
    first_name: 'Emily',
    last_name: 'Chen',
    display_name: 'Dr. Emily Chen',
    metadata: {
      test_mode: true,
      permissions: ['admin', 'read', 'write'],
      persona: 'Licensed Therapist - Busy Practice',
      status_message: 'Managing 15 active clients',
      test_scenarios: ['Client management', 'Schedule optimization', 'Content creation']
    }
  },
  'dev-returning-client': {
    id: 'dev-returning-client-001',
    email: 'sarah.johnson@dev.local',
    role: 'client',
    first_name: 'Sarah',
    last_name: 'Johnson',
    display_name: 'Sarah Johnson',
    phone: '+1-555-0187',
    metadata: {
      intake_completed: true,
      sessions_completed: 8,
      upcoming_sessions: 2,
      test_mode: true,
      persona: 'Returning Client - Regular Sessions',
      status_message: '8 sessions completed, 2 upcoming',
      test_scenarios: ['Session rebooking', 'Resource downloads', 'Progress tracking']
    }
  },
  'dev-practitioner': {
    id: 'dev-practitioner-001',
    email: 'practitioner@localhost.dev', 
    role: 'practitioner',
    first_name: 'Test',
    last_name: 'Practitioner',
    display_name: 'Test Practitioner',
    metadata: {
      test_mode: true,
      license_number: 'DEV-12345'
    }
  },
  'unauthenticated': {
    id: 'unauthenticated',
    email: '',
    role: 'client',
    display_name: 'Unauthenticated User',
    metadata: {
      test_mode: true,
      authenticated: false
    }
  }
};

// Check if we're in local development mode
export const isLocalDevMode = (): boolean => {
  // Check multiple environment indicators
  const nodeEnv = process.env.NODE_ENV || import.meta.env?.VITE_NODE_ENV;
  const isLocal = nodeEnv === 'LOCAL' || nodeEnv === 'local' || nodeEnv === 'development';
  
  // Also check if we're running on localhost
  const isLocalHost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1');
  
  const result = isLocal || (isLocalHost && nodeEnv === 'development');
  
  // Log once for debugging
  if (typeof window !== 'undefined' && !(window as any).devModeLogged) {
    console.log('🔍 Dev mode check:', {
      nodeEnv,
      isLocal,
      isLocalHost,
      result,
      hostname: window.location?.hostname
    });
    (window as any).devModeLogged = true;
  }
  
  return result;
};

// Get current dev user from localStorage or URL params
export const getCurrentDevUser = (): DevUser | null => {
  if (!isLocalDevMode()) {
    return null;
  }

  try {
    // Check URL params first (for quick switching)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const devUser = urlParams.get('dev_user');
      if (devUser && DEV_USERS[devUser]) {
        // Save to localStorage for persistence
        localStorage.setItem('dev_auth_user', devUser);
        return DEV_USERS[devUser];
      }

      // Check localStorage
      const savedUser = localStorage.getItem('dev_auth_user');
      if (savedUser && DEV_USERS[savedUser]) {
        return DEV_USERS[savedUser];
      }
    }

    // Default to client user if nothing specified
    return DEV_USERS['dev-client'];
  } catch (error) {
    console.warn('Error getting dev user:', error);
    return DEV_USERS['dev-client'];
  }
};

// Set dev user for testing
export const setDevUser = (userKey: keyof typeof DEV_USERS): void => {
  if (!isLocalDevMode()) {
    console.warn('setDevUser only works in local dev mode');
    return;
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem('dev_auth_user', userKey);
    // Reload to apply changes
    window.location.reload();
  }
};

// Create mock session for dev user
export const createMockSession = (devUser: DevUser) => {
  return {
    access_token: `dev_token_${devUser.id}`,
    refresh_token: `dev_refresh_${devUser.id}`,
    expires_in: 3600,
    token_type: 'bearer',
    user: {
      id: devUser.id,
      email: devUser.email,
      email_confirmed_at: new Date().toISOString(),
      phone: devUser.phone || '',
      confirmed_at: new Date().toISOString(),
      last_sign_in_at: new Date().toISOString(),
      app_metadata: {
        provider: 'dev-bypass',
        providers: ['dev-bypass']
      },
      user_metadata: {
        first_name: devUser.first_name,
        last_name: devUser.last_name,
        display_name: devUser.display_name,
        role: devUser.role,
        ...devUser.metadata
      },
      aud: 'authenticated',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  };
};

// Create dev authorization header
export const getDevAuthHeader = (): string | null => {
  const isDevMode = isLocalDevMode();
  if (!isDevMode) {
    console.log('🔍 getDevAuthHeader: Not in dev mode');
    return null;
  }

  const devUser = getCurrentDevUser();
  if (!devUser) {
    console.log('🔍 getDevAuthHeader: No dev user found');
    return null;
  }

  // Create a simple dev token that includes user info
  const tokenData = {
    sub: devUser.id,
    email: devUser.email,
    role: devUser.role,
    exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
    iat: Math.floor(Date.now() / 1000),
    iss: 'dev-bypass',
    aud: 'authenticated'
  };
  
  console.log('🔍 Creating dev token for:', devUser.email);
  const devToken = btoa(JSON.stringify(tokenData));

  return `Bearer dev_${devToken}`;
};

// Dev user switcher component helpers
export const getDevUserSwitcherConfig = () => {
  if (!isLocalDevMode()) {
    return null;
  }

  return {
    currentUser: getCurrentDevUser(),
    availableUsers: Object.entries(DEV_USERS).map(([key, user]) => ({
      key,
      label: `${user.display_name} (${user.role})`,
      email: user.email,
      role: user.role
    })),
    switchUser: setDevUser
  };
};

// Console helpers for dev mode
export const logDevAuthStatus = (): void => {
  if (!isLocalDevMode()) {
    return;
  }

  const devUser = getCurrentDevUser();
  // Only log once per page load to reduce spam
  if (!(window as any).devAuthLogged) {
    console.log('🔧 DEV AUTH STATUS:', {
      isLocalDevMode: true,
      currentUser: devUser,
      availableUsers: Object.keys(DEV_USERS),
      switchUserUrl: (userKey: string) => `${window.location.origin}${window.location.pathname}?dev_user=${userKey}`
    });
    (window as any).devAuthLogged = true;
  }
};

// Add to window for console access in dev
if (typeof window !== 'undefined' && isLocalDevMode()) {
  (window as any).devAuth = {
    switchUser: setDevUser,
    getCurrentUser: getCurrentDevUser,
    availableUsers: DEV_USERS,
    logStatus: logDevAuthStatus
  };
  
  console.log('🔧 Dev auth helpers available: window.devAuth');
  logDevAuthStatus();
}